'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { ChevronRight } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';

type HeroSlide = {
  _key: string;
  text: string;
  imageUrl: string;
};

type HeroSectionProps = {
  slides: HeroSlide[];
};

export default function HeroSection({ slides }: HeroSectionProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const nextSlide = useCallback(() => {
  setCurrentImageIndex((prev) => (prev + 1) % slides.length);
}, [slides.length]);

  const prevSlide = () => {
    setCurrentImageIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentImageIndex(index);
  };

  useEffect(() => {
  if (slides.length <= 1) return;
  const timer = setInterval(nextSlide, 8000);
  return () => clearInterval(timer);
}, [slides.length, nextSlide]);

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      if (diff > 50) nextSlide(); // swipe left
      else if (diff < -50) prevSlide(); // swipe right
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!slides || slides.length === 0) return null;

  return (
    <section className="relative w-full h-[40vh] md:h-[50vh] overflow-hidden">
      {/* Všechny slidy s jednotnou logikou */}
      {slides.map((slide, index) => {
        return (
          <div
            key={slide._key}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              index === currentImageIndex ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <Image
              src={slide.imageUrl}
              alt={slide.text}
              fill
              style={{ objectFit: "cover" }}
              priority={index === 0}
            />
            <div className="absolute inset-0 flex flex-col justify-end items-start p-8 md:p-12">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
              <div className="relative z-10 text-white max-w-lg space-y-2">
                <h2 className="font-serifDisplay text-4xl md:text-5xl leading-tight font-bold drop-shadow-md animate-slide-in-left">
                  {slide.text}
                </h2>
                <p className="font-sans text-xl md:text-2xl drop-shadow-md">
                  Olomouc, Přerov a okolí
                </p>
              </div>
            </div>            
          </div>
        );
      })}
       {/* Šipky */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            aria-label="Předchozí slide"
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 text-white p-4 rounded-full transition transform hover:scale-110 hover:bg-primary z-20 hover:cursor-pointer"
          >
          <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            aria-label="Další slide"
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 text-white p-4 rounded-full transition transform hover:scale-110 hover:bg-primary z-20 hover:cursor-pointer"
          >
          <ChevronRight className="w-6 h-6" />
          </button>

          {/* Indikátory */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-4 z-20">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-5 h-5 rounded-full transition ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )};
    </section>
  );

     
}
